import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PawPrint, Users, ArrowRight, MapPin, Sparkles, Copy, Clock, X, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { ensureAnonUser } from "@/lib/anonSession";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ManadaUserPage() {
  const nav = useNavigate();
  const [phase, setPhase] = useState("welcome"); // welcome | list | pick | code
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [gps, setGps] = useState(null);
  const [selected, setSelected] = useState(null); // merchant
  const [groupSize, setGroupSize] = useState(3);
  const [userName, setUserName] = useState(() => localStorage.getItem("holaradar_user_name") || "");
  const [quote, setQuote] = useState(null);
  const [code, setCode] = useState(null);

  // Skip welcome si el usuario ya lo vio
  useEffect(() => {
    if (localStorage.getItem("holaradar_manada_seen") === "1") setPhase("list");
  }, []);

  useEffect(() => {
    if (phase !== "list") return;
    (async () => {
      setLoading(true);
      try {
        await ensureAnonUser();
        let lat, lng;
        try {
          const pos = await new Promise((res, rej) =>
            navigator.geolocation.getCurrentPosition(res, rej, { enableHighAccuracy: true, timeout: 8000 })
          );
          lat = pos.coords.latitude; lng = pos.coords.longitude;
          setGps({ lat, lng });
        } catch { /* silencio — GPS opcional */ }
        const url = `${API}/api/pwa/manada/merchants` + (lat ? `?lat=${lat}&lng=${lng}` : "");
        const { data } = await axios.get(url);
        setMerchants(data.merchants || []);
      } catch {
        toast.error("No pude cargar los comercios MANADA");
      } finally {
        setLoading(false);
      }
    })();
  }, [phase]);

  // Auto-quote cuando cambia group_size
  useEffect(() => {
    if (phase !== "pick" || !selected) return;
    (async () => {
      try {
        const { data } = await axios.post(`${API}/api/pwa/manada/quote`, {
          merchant_id: selected.merchant_id,
          group_size: groupSize,
        });
        setQuote(data);
      } catch {
        setQuote(null);
      }
    })();
  }, [phase, selected, groupSize]);

  const startManada = () => {
    localStorage.setItem("holaradar_manada_seen", "1");
    setPhase("list");
  };

  const pickMerchant = (m) => {
    setSelected(m);
    setGroupSize(m.tiers?.[0]?.min_people || 3);
    setPhase("pick");
  };

  const generateCode = async (e) => {
    e.preventDefault();
    if (userName.trim().length < 3) return toast.error("Escribe tu nombre completo");
    setLoading(true);
    try {
      const device_id = localStorage.getItem("holaradar_device_id");
      localStorage.setItem("holaradar_user_name", userName.trim());
      const { data } = await axios.post(`${API}/api/pwa/manada/generate`, {
        device_id,
        merchant_id: selected.merchant_id,
        group_size: groupSize,
        user_name: userName.trim(),
      });
      setCode(data);
      setPhase("code");
      if (data.reused) toast.info(data.message);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No pude generar el código");
    } finally {
      setLoading(false);
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(code.code);
      toast.success("Código copiado");
    } catch { toast.info("Copia manualmente"); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B1120] via-neutral-950 to-black text-white">
      <header className="border-b border-white/10 bg-black/50 backdrop-blur sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#FF007A] to-[#0055FF] grid place-items-center">
              <PawPrint className="w-4 h-4" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold">HOLA RADAR</div>
              <div className="font-heading font-extrabold text-base">MANADA</div>
            </div>
          </div>
          <button onClick={() => nav("/")} className="text-neutral-400 hover:text-white p-2" aria-label="Cerrar">
            <X className="w-5 h-5" />
          </button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto p-4 md:p-8">
        {phase === "welcome" && <Welcome onContinue={startManada} />}
        {phase === "list" && <MerchantList merchants={merchants} loading={loading} gps={gps} onPick={pickMerchant} />}
        {phase === "pick" && selected && (
          <PickFlow
            merchant={selected}
            groupSize={groupSize}
            setGroupSize={setGroupSize}
            quote={quote}
            userName={userName}
            setUserName={setUserName}
            onGenerate={generateCode}
            onBack={() => { setPhase("list"); setSelected(null); setCode(null); }}
            loading={loading}
          />
        )}
        {phase === "code" && code && (
          <CodeView code={code} merchant={selected} onCopy={copyCode} onBack={() => setPhase("list")} />
        )}
      </div>
    </div>
  );
}

function Welcome({ onContinue }) {
  return (
    <div className="space-y-6">
      <div className="rounded-3xl bg-gradient-to-br from-[#FF007A] to-[#0055FF] p-8 shadow-2xl relative overflow-hidden">
        <div className="absolute -right-6 -top-6 opacity-20"><PawPrint className="w-40 h-40" /></div>
        <div className="text-[10px] uppercase tracking-widest font-bold opacity-80">Bienvenido a</div>
        <h1 className="font-heading font-extrabold text-4xl md:text-5xl mt-1 leading-tight">MANADA</h1>
        <p className="text-base mt-4 leading-relaxed opacity-95">
          Entre más grande la <strong>MANADA</strong>, más ahorras. Cada comercio decide su descuento por grupo y tú generas un código único para canjearlo en caja.
        </p>
      </div>

      <div className="space-y-3">
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4 flex gap-3">
          <div className="w-8 h-8 rounded-full bg-[#FF007A] grid place-items-center shrink-0 font-heading font-extrabold text-sm">1</div>
          <div>
            <div className="font-bold text-sm">Elige un comercio MANADA cerca de ti</div>
            <div className="text-xs text-neutral-400 mt-0.5">Restaurantes, cafeterías, salones, spas… los que activaron MANADA.</div>
          </div>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4 flex gap-3">
          <div className="w-8 h-8 rounded-full bg-[#FF007A] grid place-items-center shrink-0 font-heading font-extrabold text-sm">2</div>
          <div>
            <div className="font-bold text-sm">Dinos con cuántas personas más vas</div>
            <div className="text-xs text-neutral-400 mt-0.5">Si van 4 tienes cierto descuento, si van 6 mejor descuento. Cada comercio pone sus tramos.</div>
          </div>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4 flex gap-3">
          <div className="w-8 h-8 rounded-full bg-[#FF007A] grid place-items-center shrink-0 font-heading font-extrabold text-sm">3</div>
          <div>
            <div className="font-bold text-sm">Recibes un código único</div>
            <div className="text-xs text-neutral-400 mt-0.5">Al pagar, dile al mesero tu código y aplica el descuento al ticket. Válido 24 horas.</div>
          </div>
        </div>
      </div>

      <Button onClick={onContinue} className="w-full h-14 rounded-full bg-[#FF007A] hover:bg-[#e60070] font-bold text-base" data-testid="manada-welcome-continue">
        <Sparkles className="w-4 h-4" /> Ver comercios MANADA
        <ArrowRight className="w-4 h-4" />
      </Button>
    </div>
  );
}

function MerchantList({ merchants, loading, gps, onPick }) {
  if (loading) return <div className="text-center py-12 text-neutral-400">Cargando comercios MANADA…</div>;
  if (merchants.length === 0) return (
    <div className="text-center py-12">
      <PawPrint className="w-12 h-12 mx-auto text-neutral-600 mb-3" />
      <div className="font-heading font-extrabold text-lg">Aún no hay comercios MANADA cerca</div>
      <p className="text-sm text-neutral-400 mt-1 max-w-md mx-auto">Cuando un comercio active MANADA, aparecerá aquí. Vuelve pronto.</p>
    </div>
  );
  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-heading font-extrabold text-2xl">Comercios MANADA</h2>
        <p className="text-sm text-neutral-400 mt-1">{gps ? "Ordenados por cercanía a ti." : "Activa tu ubicación para ver los más cercanos."}</p>
      </div>
      <div className="space-y-3" data-testid="manada-merchant-list">
        {merchants.map((m) => (
          <button
            key={m.merchant_id}
            onClick={() => onPick(m)}
            className="w-full text-left rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 hover:border-[#FF007A]/40 transition p-4 flex gap-3 items-center"
            data-testid={`manada-merchant-${m.merchant_id}`}
          >
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#FF007A] to-[#0055FF] grid place-items-center shrink-0">
              {m.picture ? <img src={m.picture} alt="" className="w-full h-full rounded-xl object-cover" /> : <PawPrint className="w-6 h-6" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-heading font-extrabold text-base truncate">{m.business_name}</div>
              <div className="text-xs text-neutral-400 truncate">{m.category}{m.address ? ` · ${m.address}` : ""}</div>
              <div className="text-xs text-[#FF007A] font-bold mt-1">Hasta {m.best_discount_percent}% de descuento en grupo</div>
            </div>
            {m.distance_m != null && (
              <div className="text-right text-xs text-neutral-500 shrink-0">
                <MapPin className="w-3 h-3 inline" /> {m.distance_m < 1000 ? `${Math.round(m.distance_m)}m` : `${(m.distance_m / 1000).toFixed(1)}km`}
              </div>
            )}
            <ArrowRight className="w-4 h-4 text-neutral-500 shrink-0" />
          </button>
        ))}
      </div>
    </div>
  );
}

function PickFlow({ merchant, groupSize, setGroupSize, quote, userName, setUserName, onGenerate, onBack, loading }) {
  const tiers = merchant.tiers || [];
  const nextTier = tiers.find((t) => t.min_people > groupSize);
  return (
    <div className="space-y-5">
      <button onClick={onBack} className="text-sm text-neutral-400 hover:text-white flex items-center gap-1">
        ← Volver
      </button>
      <div>
        <div className="text-xs uppercase tracking-widest text-neutral-500 font-bold">Comercio elegido</div>
        <h2 className="font-heading font-extrabold text-2xl">{merchant.business_name}</h2>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
        <Label className="text-xs text-neutral-400">¿Con cuántas personas van en total?</Label>
        <div className="flex items-center gap-3 mt-2">
          <Users className="w-5 h-5 text-neutral-400" />
          <button onClick={() => setGroupSize(Math.max(2, groupSize - 1))} className="w-10 h-10 rounded-full bg-white/10 font-bold hover:bg-white/20">−</button>
          <div className="font-heading font-extrabold text-4xl w-16 text-center" data-testid="manada-group-size">{groupSize}</div>
          <button onClick={() => setGroupSize(Math.min(99, groupSize + 1))} className="w-10 h-10 rounded-full bg-white/10 font-bold hover:bg-white/20">+</button>
          <div className="text-sm text-neutral-500">personas</div>
        </div>

        <div className="mt-5">
          {quote?.applicable_tier ? (
            <div className="rounded-xl bg-gradient-to-br from-[#FF007A]/20 to-[#0055FF]/20 border border-[#FF007A]/30 p-4">
              <div className="text-xs uppercase tracking-widest font-bold opacity-80">Tu descuento MANADA</div>
              <div className="font-heading font-extrabold text-5xl mt-1 text-[#FF007A]" data-testid="manada-quote-percent">
                {quote.applicable_tier.discount_percent}%
              </div>
              <div className="text-sm text-neutral-300 mt-1">de descuento en tu ticket</div>
              {nextTier && (
                <div className="mt-3 text-xs text-neutral-400">
                  💡 Con <strong className="text-white">{nextTier.min_people}</strong> personas obtienes <strong className="text-[#FF007A]">{nextTier.discount_percent}%</strong>
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 p-3 text-sm text-amber-200">
              Con esta cantidad todavía no hay descuento. Necesitas al menos {(tiers[0] || {}).min_people || "más"} personas.
            </div>
          )}
        </div>

        {/* Tabla de tramos */}
        <div className="mt-4 text-xs text-neutral-500">Tramos de este comercio:</div>
        <div className="mt-1 flex flex-wrap gap-2">
          {tiers.map((t) => (
            <div key={`tier-${t.min_people}`} className={`px-3 py-1.5 rounded-full text-xs border ${groupSize >= t.min_people ? "border-[#FF007A]/40 bg-[#FF007A]/10 text-[#FF007A] font-bold" : "border-white/10 text-neutral-500"}`}>
              {t.min_people}+ pers · {t.discount_percent}%
            </div>
          ))}
        </div>
      </div>

      {quote?.applicable_tier && (
        <form onSubmit={onGenerate} className="rounded-2xl border border-white/10 bg-white/5 p-5 space-y-3">
          <div>
            <Label className="text-xs text-neutral-400">Tu nombre completo (para el mesero)</Label>
            <Input
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              placeholder="Ej: Ana López"
              className="mt-1 h-11 rounded-xl bg-white/10 border-white/20 text-white"
              required
              minLength={3}
              maxLength={120}
              data-testid="manada-user-name"
            />
          </div>
          <Button type="submit" disabled={loading} className="w-full h-12 rounded-full bg-[#FF007A] hover:bg-[#e60070] font-bold" data-testid="manada-generate-btn">
            <Sparkles className="w-4 h-4" /> {loading ? "Generando…" : "Aceptar oferta y generar código"}
          </Button>
        </form>
      )}
    </div>
  );
}

function CodeView({ code, merchant, onCopy, onBack }) {
  const [secondsLeft, setSecondsLeft] = useState(0);
  useEffect(() => {
    const tick = () => {
      const diff = Math.max(0, (new Date(code.expires_at).getTime() - Date.now()) / 1000);
      setSecondsLeft(Math.floor(diff));
    };
    tick();
    const i = setInterval(tick, 1000);
    return () => clearInterval(i);
  }, [code.expires_at]);
  const hh = String(Math.floor(secondsLeft / 3600)).padStart(2, "0");
  const mm = String(Math.floor((secondsLeft % 3600) / 60)).padStart(2, "0");
  const ss = String(secondsLeft % 60).padStart(2, "0");

  return (
    <div className="space-y-5">
      <div className="rounded-3xl bg-gradient-to-br from-[#FF007A] to-[#0055FF] p-8 text-center shadow-2xl">
        <div className="text-[10px] uppercase tracking-widest font-bold opacity-80">Tu código MANADA</div>
        <div className="font-heading font-extrabold text-5xl md:text-6xl mt-2 font-mono tracking-wider" data-testid="manada-code-display">
          {code.code}
        </div>
        <button onClick={onCopy} className="mt-3 text-xs bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-full inline-flex items-center gap-1">
          <Copy className="w-3 h-3" /> Copiar código
        </button>
        <div className="mt-5 grid grid-cols-3 gap-2 text-white/90">
          <div><div className="text-[10px] opacity-70 uppercase font-bold">Descuento</div><div className="font-heading font-extrabold text-2xl">{code.discount_percent}%</div></div>
          <div><div className="text-[10px] opacity-70 uppercase font-bold">Grupo</div><div className="font-heading font-extrabold text-2xl">{code.group_size}</div></div>
          <div><div className="text-[10px] opacity-70 uppercase font-bold">Vence en</div><div className="font-heading font-extrabold text-lg tabular-nums">{hh}:{mm}:{ss}</div></div>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-sm space-y-2">
        <div className="flex items-center gap-2 text-neutral-300">
          <CheckCircle2 className="w-4 h-4 text-green-400" />
          Muestra este código al mesero de <strong className="text-white">{merchant?.business_name}</strong> al pagar.
        </div>
        <div className="flex items-center gap-2 text-neutral-300">
          <Clock className="w-4 h-4 text-amber-400" />
          Válido durante 24 horas. Después expira automáticamente.
        </div>
      </div>

      <Button onClick={onBack} variant="outline" className="w-full h-11 rounded-full border-white/20 text-white bg-white/5 hover:bg-white/10">
        Ver más comercios MANADA
      </Button>
    </div>
  );
}
